import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { audioService } from "@/core/audio/audioService";
import { useBingoSettingsStore } from "@/plugins/bingo/state/bingoSettingsStore";
import { getBallTheme } from "./ballTheme";
import MagicTrail from "./MagicTrail";
import { getPhase2Special } from "@/plugins/bingo/projector/specials/phase2Specials";

/**
 * BingoBall — the 6-stage cinematic standard-ball reveal.
 *
 *   Stage A  0.00 → 0.30s   Ball SCALES IN spinning fast (letter shown)
 *   Stage B  0.30 → 1.20s   Ball ORBITS with spark particles (letter shown)
 *   Stage C  1.20 → 1.55s   Ball OPENS — top & bottom halves rotate apart,
 *                            bright core flash between them
 *   Stage D  1.55 → 1.95s   NUMBER SNAPS INTO PLACE inside the ball window
 *                            (halves close back with the number now locked)
 *   Stage E  1.95 → 2.35s   MAGIC TRAIL streaks from the ball to the target
 *                            board cell
 *   Stage F  2.35s+         Board cell lights up, ball stays sacred
 *
 * Total ≈ 2.4s. Number is locked once revealed — it never morphs.
 *
 * Props:
 *   value             — the bingo number being revealed
 *   onCellLightReady  — called when the trail lands (parent uses this to
 *                       promote the board cell to "lit up")
 */
export default function BingoBall({
  value,
  onCellLightReady,
  /**
   * Namespace prefix for internal data-testids so multiple BingoBall
   * instances can coexist on the page without their MagicTrail queries
   * colliding. The live Projector uses "projector" (default). The
   * Preview Animation overlay uses "preview".
   */
  testidPrefix = "projector",
}) {
  const [stage, setStage] = useState("A"); // A → B → C → D → E → F
  const [trailActive, setTrailActive] = useState(false);
  const theme = getBallTheme(value);

  // Reveal-timing config (host-adjustable, cross-window synced).
  const timing = useBingoSettingsStore((s) => s.settings.revealTiming);
  const specials = useBingoSettingsStore((s) => s.settings.specials);
  const {
    ballAppearMs,
    ballSpinMs,
    ballOpenMs,
    revealPauseMs,
    magicTrailMs,
  } = timing;

  // Phase 2 signature overlay for this value (if any and enabled).
  const phase2 = getPhase2Special(value);
  const phase2Enabled = phase2 ? specials?.[phase2.key] !== false : false;
  const Phase2Component = phase2Enabled ? phase2.Component : null;
  // Signature activates the moment the number is revealed (stage D+).
  const phase2Active =
    !!Phase2Component &&
    (stage === "D" || stage === "E" || stage === "F");

  // Milestone offsets from t=0 (call fired).
  const tSpin = ballAppearMs;
  const tOpen = tSpin + ballSpinMs;
  const tReveal = tOpen + ballOpenMs;
  const tTrail = tReveal + revealPauseMs;

  // Play the reveal sound the moment the ball opens (stage C).
  // Ball whoosh at stage A, ding at stage D.
  useEffect(() => {
    if (value === null || value === undefined) return;
    setStage("A");
    setTrailActive(false);

    audioService.playReveal(); // ball whoosh / appear

    const timers = [];
    timers.push(setTimeout(() => setStage("B"), tSpin));
    timers.push(setTimeout(() => setStage("C"), tOpen));
    timers.push(setTimeout(() => setStage("D"), tReveal));
    timers.push(
      setTimeout(() => {
        setStage("E");
        setTrailActive(true);
      }, tTrail),
    );
    // Stage F handled when trail completes → onDone
    return () => timers.forEach((t) => clearTimeout(t));
  }, [value, tSpin, tOpen, tReveal, tTrail]);

  if (value === null || value === undefined) return null;

  // Faces shown inside the ball window: letter until the moment of reveal,
  // then the sacred number from stage D onward.
  const showNumber = stage === "D" || stage === "E" || stage === "F";

  const openAngle = stage === "C" ? 42 : 0;

  return (
    <div
      data-testid="projector-ball"
      className="relative flex items-center justify-center"
      style={{ width: "clamp(20rem, 32vw, 34rem)", height: "clamp(20rem, 32vw, 34rem)" }}
    >
      {/* Ambient glow behind the ball, brightens on open + reveal */}
      <div
        aria-hidden
        className="absolute inset-[-15%] pointer-events-none rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${theme.glow} 0%, transparent 60%)`,
          opacity: stage === "C" || stage === "D" ? 1 : 0.55,
          transition: "opacity 300ms ease",
        }}
      />

      {/* Orbital spark particles (stages A → D) */}
      {(stage === "A" || stage === "B" || stage === "C") && (
        <OrbitalSparks color={theme.core} />
      )}

      {/* The ball itself — spring-scale in, then continuous spin until open */}
      <motion.div
        key={`ball-${value}`}
        className="relative"
        style={{
          width: "82%",
          height: "82%",
          transformStyle: "preserve-3d",
          perspective: "1200px",
        }}
        initial={{ scale: 0.15, opacity: 0 }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        transition={{
          scale: {
            type: "spring",
            stiffness: 300,
            damping: 18,
            delay: 0,
            duration: ballAppearMs / 1000,
          },
          opacity: { duration: Math.min(0.35, ballAppearMs / 1000) },
        }}
      >
        <motion.div
          className="absolute inset-0"
          animate={{
            rotate:
              stage === "A"
                ? [0, 360, 720]
                : stage === "B"
                  ? [720, 900, 1080]
                  : 1080,
          }}
          transition={{
            duration:
              stage === "A"
                ? ballAppearMs / 1000
                : stage === "B"
                  ? ballSpinMs / 1000
                  : ballOpenMs / 1000,
            ease: stage === "A" ? "easeOut" : "linear",
          }}
        >
          {/* Sphere shell (rendered as two halves that split on stage C) */}
          <BallShell
            theme={theme}
            openAngle={openAngle}
            openMs={ballOpenMs}
            showNumber={showNumber}
            value={value}
          />
        </motion.div>

        {/* Center anchor used by MagicTrail as the source point */}
        <span
          data-testid={`${testidPrefix}-ball-anchor`}
          aria-hidden
          className="absolute left-1/2 top-1/2"
          style={{
            width: 1,
            height: 1,
            transform: "translate(-50%, -50%)",
          }}
        />
      </motion.div>

      {/* Magic trail — mounted only during stage E */}
      <AnimatePresence>
        {trailActive && (
          <MagicTrail
            key={`trail-${value}`}
            value={value}
            color={theme.core}
            sourceSelector={`[data-testid="${testidPrefix}-ball-anchor"]`}
            targetSelector={`[data-testid="${testidPrefix}-cell-${value}"]`}
            durationMs={magicTrailMs}
            onDone={() => {
              setTrailActive(false);
              setStage("F");
              onCellLightReady?.(value);
            }}
          />
        )}
      </AnimatePresence>

      {/* Phase 2 signature overlay — layers on top of the ball reveal
          without modifying the sacred revealed number. */}
      {Phase2Component && <Phase2Component active={phase2Active} value={value} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Internals                                                           */
/* ------------------------------------------------------------------ */

function BallShell({ theme, openAngle, openMs, showNumber, value }) {
  const openDurSec = (openMs || 350) / 1000;
  return (
    <div className="absolute inset-0" style={{ transformStyle: "preserve-3d" }}>
      {/* Bottom half (base) — kept behind, opens downward */}
      <motion.div
        className="absolute inset-0"
        style={{
          clipPath: "inset(50% 0 0 0)",
          transformOrigin: "50% 50%",
        }}
        animate={{ rotateX: openAngle * 0.9 }}
        transition={{ duration: openDurSec, ease: "easeOut" }}
      >
        <Sphere theme={theme} />
      </motion.div>

      {/* Bright inner core visible during opening — sacred number stage */}
      <motion.div
        aria-hidden
        className="absolute inset-[8%] rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 45%, #ffffff 0%, ${theme.highlight} 30%, ${theme.core} 75%, transparent 100%)`,
          filter: "blur(6px)",
        }}
        animate={{
          opacity: openAngle > 0 ? 1 : 0,
          scale: openAngle > 0 ? 1 : 0.4,
        }}
        transition={{ duration: Math.min(0.25, openDurSec * 0.9) }}
      />

      {/* Center window with number (blank during spin — classic bingo ball) */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ transform: "translateZ(1px)" }}
      >
        <motion.div
          className="rounded-full flex items-center justify-center relative overflow-hidden"
          style={{
            width: "58%",
            height: "58%",
            background:
              "radial-gradient(circle at 42% 32%, #ffffff 0%, #fafafa 55%, #d4d4d8 100%)",
            boxShadow:
              "inset 0 3px 10px rgba(255,255,255,0.9), inset 0 -6px 16px rgba(0,0,0,0.15), 0 0 30px rgba(0,0,0,0.35)",
          }}
        >
          <AnimatePresence mode="popLayout">
            {showNumber ? (
              <motion.span
                key={`num-${value}`}
                data-testid="projector-ball-number"
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 18,
                }}
                className="ic-font-display font-black tabular-nums select-none"
                style={{
                  color: "#0a0a0a",
                  fontSize: "clamp(6rem, 12vw, 14rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                }}
              >
                {Number(value) === 67 ? (
                  <SixSevenDigitDance />
                ) : Number(value) === 69 ? (
                  <SixNineDigitFlip />
                ) : (
                  value
                )}
              </motion.span>
            ) : (
              // Classic pre-reveal state: subtle shimmer only, no letter.
              <motion.span
                key="shimmer"
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.35, 0.7, 0.35] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-[15%] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 65%)",
                  filter: "blur(6px)",
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Top half — opens upward, occludes the center until stage C */}
      <motion.div
        className="absolute inset-0"
        style={{
          clipPath: "inset(0 0 50% 0)",
          transformOrigin: "50% 50%",
        }}
        animate={{ rotateX: -openAngle }}
        transition={{ duration: openDurSec, ease: "easeOut" }}
      >
        <Sphere theme={theme} />
      </motion.div>
    </div>
  );
}

/**
 * Sphere — a glossy 3D-looking bingo ball. Both halves render this;
 * clip-path decides which half is visible per shell layer.
 */
function Sphere({ theme }) {
  return (
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: `radial-gradient(circle at 32% 28%, ${theme.highlight} 0%, ${theme.core} 42%, ${theme.shadow} 100%)`,
        boxShadow: `
          inset 0 10px 25px rgba(255,255,255,0.35),
          inset 0 -22px 45px rgba(0,0,0,0.45),
          0 25px 60px rgba(0,0,0,0.55),
          0 0 45px ${theme.glow}
        `,
      }}
    >
      {/* Specular highlight */}
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          top: "10%",
          left: "22%",
          width: "26%",
          height: "18%",
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.75) 0%, transparent 70%)",
          filter: "blur(2px)",
        }}
      />
      {/* Thin equator line to sell the "two halves" idea when closed */}
      <div
        aria-hidden
        className="absolute left-0 right-0"
        style={{
          top: "49.6%",
          height: "0.8%",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.35) 20%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.35) 80%, transparent 100%)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.1)",
        }}
      />
    </div>
  );
}

/**
 * SixSevenDigitDance — after the sacred number 67 has settled inside the
 * ball, the digits perform an elegant, cinematic post-reveal dance:
 *
 *   Phase 1 (0.40s)   6 glides UP · 7 glides DOWN
 *   Phase 2 (0.40s)   6 glides DOWN · 7 glides UP
 *   Phase 3 (0.20s)   Both return together to perfectly form "67"
 *
 * The dance starts AFTER a short settle delay so the sacred spring-in
 * completes first. The number itself is NEVER swapped or morphed —
 * only translated vertically. Amplitude is expressed as a percentage of
 * each digit's own height so it scales gracefully with font size.
 */
function SixSevenDigitDance() {
  const settleDelay = 0.35; // seconds — sacred settle before dance begins
  const p1 = 0.4;
  const p2 = 0.4;
  const p3 = 0.2;
  const total = settleDelay + p1 + p2 + p3;
  const tSettle = settleDelay / total;
  const tPhase1End = (settleDelay + p1) / total;
  const tPhase2End = (settleDelay + p1 + p2) / total;
  const amp = "18%"; // subtle, elegant vertical amplitude
  const negAmp = "-18%";

  return (
    <>
      <motion.span
        data-testid="projector-ball-digit-6"
        style={{ display: "inline-block", willChange: "transform" }}
        initial={{ y: 0 }}
        animate={{ y: ["0%", "0%", negAmp, amp, "0%"] }}
        transition={{
          duration: total,
          times: [0, tSettle, tPhase1End, tPhase2End, 1],
          ease: "easeInOut",
        }}
      >
        6
      </motion.span>
      <motion.span
        data-testid="projector-ball-digit-7"
        style={{ display: "inline-block", willChange: "transform" }}
        initial={{ y: 0 }}
        animate={{ y: ["0%", "0%", amp, negAmp, "0%"] }}
        transition={{
          duration: total,
          times: [0, tSettle, tPhase1End, tPhase2End, 1],
          ease: "easeInOut",
        }}
      >
        7
      </motion.span>
    </>
  );
}

/**
 * SixNineDigitFlip — after the sacred 69 has settled inside the ball,
 * the whole "69" performs an elegant, cinematic 180° rotation around
 * its own center. Because 6 rotated 180° reads as 9 and vice versa,
 * the ball's face STILL reads "69" throughout the flip — the visual
 * gag the "Shy Ball" nickname is built on.
 *
 * Choreography (starts after the sacred settle so the reveal is
 * never disturbed):
 *
 *   Phase 1 (0.60s)   Gentle 180° rotation of the digit pair.
 *   Phase 2 (0.40s)   Hold at 180° — still reads "69".
 *   Phase 3 (0.60s)   Rotate back to 0° — original orientation.
 *
 * The number is only translated/rotated — never swapped, never
 * replaced. The sacred-number rule is preserved.
 */
function SixNineDigitFlip() {
  const settleDelay = 0.35; // seconds — sacred settle before flip begins
  const p1 = 0.6;
  const p2 = 0.4;
  const p3 = 0.6;
  const total = settleDelay + p1 + p2 + p3;
  const tSettle = settleDelay / total;
  const tPhase1End = (settleDelay + p1) / total;
  const tPhase2End = (settleDelay + p1 + p2) / total;

  return (
    <motion.span
      data-testid="projector-ball-shyball-flip"
      style={{
        display: "inline-block",
        transformOrigin: "50% 50%",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
      initial={{ rotate: 0 }}
      animate={{ rotate: [0, 0, 180, 180, 0] }}
      transition={{
        duration: total,
        times: [0, tSettle, tPhase1End, tPhase2End, 1],
        ease: "easeInOut",
      }}
    >
      <span data-testid="projector-ball-digit-6">6</span>
      <span data-testid="projector-ball-digit-9">9</span>
    </motion.span>
  );
}

/**
 * OrbitalSparks — 10 sparks orbiting the ball on two axes with a slight
 * tilt to feel three-dimensional. Fades out after ~0.9s so it doesn't
 * compete with the reveal.
 */
function OrbitalSparks({ color }) {
  const sparks = Array.from({ length: 10 });
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{ transform: "translateZ(0)" }}
    >
      {sparks.map((_, i) => {
        const angle = (i / sparks.length) * Math.PI * 2;
        const radius = 46;
        const tilt = i % 2 === 0 ? 22 : -22;
        return (
          <motion.span
            key={i}
            className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: 8,
              height: 8,
              background: color,
              boxShadow: `0 0 12px ${color}, 0 0 22px ${color}`,
              translateX: "-50%",
              translateY: "-50%",
            }}
            initial={{
              x: Math.cos(angle) * radius + "%",
              y: Math.sin(angle) * radius + "%",
              opacity: 0,
            }}
            animate={{
              rotate: 720,
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              rotate: { duration: 1.4, ease: "linear" },
              opacity: { duration: 1.4, times: [0, 0.1, 0.85, 1] },
              delay: i * 0.03,
            }}
          >
            {/* nested transform for elliptical orbit look */}
            <motion.span
              className="block w-full h-full rounded-full"
              style={{ background: "inherit", transform: `rotateZ(${tilt}deg)` }}
            />
          </motion.span>
        );
      })}
    </div>
  );
}
